import { Directive, ElementRef, OnDestroy, inject, input, signal } from "@angular/core";
import { CdkDrag } from "@angular/cdk/drag-drop";

type ResizeCorner = "nw" | "ne" | "sw" | "se";

/**
 * Adds drag-aware corner resizing to a floating chat overlay panel.
 * Must be applied on the same host element as `cdkDrag`. Tracks whether the
 * panel has been moved/resized (`isDirty`) and can restore it via `reset()`.
 */
@Directive({
  selector: "[libChatOverlayResize]",
  standalone: true,
  exportAs: "libChatOverlayResize",
})
export class ChatOverlayResizeDirective implements OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  private readonly drag = inject(CdkDrag);

  readonly minWidth = input<number>(300);

  readonly minHeight = input<number>(380);

  private readonly _isDirty = signal(false);

  readonly isDirty = this._isDirty.asReadonly();

  private resizeStartX?: number;

  private resizeStartY?: number;

  private resizeStartW?: number;

  private resizeStartH?: number;

  private resizeStartDragX?: number;

  private resizeStartDragY?: number;

  private activeResizeCorner?: ResizeCorner;

  private readonly boundResizeMove = this.onResizeMove.bind(this);

  private readonly boundResizeUp = this.onResizeUp.bind(this);

  private paneElement?: HTMLElement | null;

  /** Original CDK connected-position anchor (right/bottom-based), captured once so `reset()` can restore it. */
  private originalPaneAnchor?: { left: string; top: string; right: string; bottom: string };

  constructor() {
    this.drag.ended.subscribe(() => this._isDirty.set(true));
  }

  ngOnDestroy(): void {
    document.removeEventListener("mousemove", this.boundResizeMove);
    document.removeEventListener("mouseup", this.boundResizeUp);
  }

  onResizeStart(event: MouseEvent, corner: ResizeCorner): void {
    event.preventDefault();
    this.lockPaneToFixedAnchor();
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const dragPos = this.drag.getFreeDragPosition();
    this.activeResizeCorner = corner;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartW = rect.width;
    this.resizeStartH = rect.height;
    this.resizeStartDragX = dragPos.x;
    this.resizeStartDragY = dragPos.y;
    document.addEventListener("mousemove", this.boundResizeMove);
    document.addEventListener("mouseup", this.boundResizeUp);
  }

  reset(): void {
    this.drag.reset();
    this.elementRef.nativeElement.style.width = "";
    this.elementRef.nativeElement.style.height = "";
    this.restoreOriginalAnchor();
    this._isDirty.set(false);
  }

  /**
   * The connected overlay anchors the pane with CSS `right`/`bottom` (per `chatPanelPositions`),
   * which already repositions the top-left corner natively as the pane's size changes. Resizing
   * without neutralizing that anchor first would double-apply the corner-compensation transform
   * below, making the panel drift across the viewer. Converting to an equivalent fixed
   * `left`/`top` (same on-screen position, anchor cleared) makes this directive the sole owner
   * of position during the drag.
   */
  private lockPaneToFixedAnchor(): void {
    const pane = this.getPaneElement();
    if (!pane) return;

    if (!this.originalPaneAnchor) {
      this.originalPaneAnchor = {
        left: pane.style.left,
        top: pane.style.top,
        right: pane.style.right,
        bottom: pane.style.bottom,
      };
    }

    const rect = pane.getBoundingClientRect();
    const dragPos = this.drag.getFreeDragPosition();
    pane.style.left = `${rect.left - dragPos.x}px`;
    pane.style.top = `${rect.top - dragPos.y}px`;
    pane.style.right = "auto";
    pane.style.bottom = "auto";
  }

  private restoreOriginalAnchor(): void {
    const pane = this.getPaneElement();
    if (!pane || !this.originalPaneAnchor) return;

    pane.style.left = this.originalPaneAnchor.left;
    pane.style.top = this.originalPaneAnchor.top;
    pane.style.right = this.originalPaneAnchor.right;
    pane.style.bottom = this.originalPaneAnchor.bottom;
  }

  private getPaneElement(): HTMLElement | null {
    if (this.paneElement === undefined) {
      this.paneElement = this.elementRef.nativeElement.closest(".cdk-overlay-pane") as HTMLElement | null;
    }
    return this.paneElement;
  }

  private onResizeMove(event: MouseEvent): void {
    if (this.resizeStartX === undefined) return;
    const dx = event.clientX - this.resizeStartX;
    const dy = event.clientY - this.resizeStartY!;

    let rawW = this.resizeStartW!;
    let rawH = this.resizeStartH!;

    switch (this.activeResizeCorner) {
      case "nw": rawW -= dx; rawH -= dy; break;
      case "ne": rawW += dx; rawH -= dy; break;
      case "sw": rawW -= dx; rawH += dy; break;
      case "se": rawW += dx; rawH += dy; break;
    }

    const newW = Math.max(this.minWidth(), rawW);
    const newH = Math.max(this.minHeight(), rawH);
    const wDelta = newW - this.resizeStartW!;
    const hDelta = newH - this.resizeStartH!;

    // For left-side corners (nw/sw): shift pane left so the right edge stays fixed
    const newDragX = (this.activeResizeCorner === "nw" || this.activeResizeCorner === "sw")
      ? this.resizeStartDragX! - wDelta
      : this.resizeStartDragX!;

    // For top corners (nw/ne): shift pane up so the bottom edge stays fixed
    const newDragY = (this.activeResizeCorner === "nw" || this.activeResizeCorner === "ne")
      ? this.resizeStartDragY! - hDelta
      : this.resizeStartDragY!;

    this.elementRef.nativeElement.style.width = `${newW}px`;
    this.elementRef.nativeElement.style.height = `${newH}px`;
    this.drag.setFreeDragPosition({ x: newDragX, y: newDragY });
    this._isDirty.set(true);
  }

  private onResizeUp(): void {
    document.removeEventListener("mousemove", this.boundResizeMove);
    document.removeEventListener("mouseup", this.boundResizeUp);
    this.resizeStartX = undefined;
    this.activeResizeCorner = undefined;
  }
}
